// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck TODO[redux-toolkit-upgrade]: Fix all type errors

import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import * as routes from 'api';
import { postJSON } from 'api/fetch';
import { pollWorkerStatus } from 'api/workerStatus';
import { captureException } from 'fe-core/util/errorTracking';
import { difference } from 'lodash';

import { actionTypes as addTeamActions } from 'actions/addTeam';
import { actionTypes as employeeViewActionTypes } from 'actions/employeeView';
import { actionTypes as timeOffsActions } from 'actions/timeOff';

import { fetchSchedule } from 'features/scheduleBuilder/ScheduleBuilderView/actions/shared';
import { fetchCompanyHours } from 'features/scheduleBuilder/slices/companyHours/slice';
import { fetchRoleLaborTotals } from 'features/scheduleBuilder/slices/roleLaborTotals/slice';
import { updateShift } from 'features/scheduleBuilder/slices/shifts/actions';
import { createShift } from 'features/scheduleBuilder/slices/shifts/slice';

import { toI18n } from 'util/i18n';
import { getObjectFromLocalStorage } from 'util/localStorageWrapper';
import { trackUxEvent } from 'util/tracking';
import {
  EVENT_ACTIONS,
  EVENT_CATEGORIES,
  PRODUCT_AREAS,
} from 'util/tracking_constants';

import { SYNC_STATES } from '../types';

import {
  DIALOGS,
  DRAWERS,
  EMPLOYEE_VIEW_FILTERS,
  FILTERS,
  MODALS,
  SLICE_NAME,
  VIEW_BY,
  VIEW_BY_DAY,
} from './constants';
import { getInitialStateFromLS } from './helpers';
import {
  NarrativeMessageProps,
  PublishScheduleProps,
  ShiftSyncStatusProps,
  SyncShiftProps,
} from './types';

/* eslint-disable @typescript-eslint/no-explicit-any */
const setDrawer = (state: any, drawer: any, uxContext = {}) => {
  state.activeDrawer = drawer;
  state.drawerUxContext = uxContext;
};

/* eslint-disable @typescript-eslint/no-explicit-any */
const setModal = (state: any, modal: any, uxContext = {}) => {
  state.activeModal = modal;
  state.drawerUxContext = uxContext;
};

/* eslint-disable @typescript-eslint/no-explicit-any */
const setDialog = (state: any, dialog: any, uxContext = {}) => {
  state.activeDialog = dialog;
  state.drawerUxContext = uxContext;
};

const checkForJobUpdate = (state: any, { payload }: { payload: any }) => {
  const { id, type } = payload.data.relationships.owner.data;
  if (type === 'job') {
    state.pendingJobLaborTotalsIds.push(id);
  }
};

const slice = createSlice({
  name: SLICE_NAME,
  initialState: {
    currentDateRange: null,
    conflictsEnabled: false,
    viewType: 'employee',
    rangeType: 'week',
    searchTerm: '',
    hasPendingData: false,
    isJumpstartClosed: false,
    pendingJobLaborTotalsIds: [] as number[],
    companyLaborTotals: [],
    activeDrawer: null,
    activeModal: undefined,
    activeDialog: undefined,
    employeeViewFilter: EMPLOYEE_VIEW_FILTERS.ALL,
    departmentId: null,
    notificationCenterMessage: null,
    notificationCenterType: null,
    notificationCenterMessageAI: false,
    customJobIds: [],
    isAvailabilitiesVisible: getInitialStateFromLS(
      FILTERS.IS_AVAILABILITIES_VISIBLE
    ),
    isTimeOffsVisible: getInitialStateFromLS(FILTERS.IS_TIME_OFFS_VISIBLE),
    isShiftsAtThisLocationOnly:
      getObjectFromLocalStorage(FILTERS.IS_SHIFTS_AT_THIS_LOCATION_ONLY) ||
      false,
    copyWeekModalIsOpen: false,
    sortByDay: getObjectFromLocalStorage(VIEW_BY_DAY) || VIEW_BY.firstName,
    copyPasteShift: null,
    syncErrors: [],
    syncShiftStatus: [] as ShiftSyncStatusProps[],
    partnerSyncState: undefined as SYNC_STATES,
    schedulePredictionGeneration: false,
    predictedShiftsCount: 0,
    predictionBaseStartDate: undefined,
    predictionBaseEndDate: undefined,
    publishedCount: 0,
    publishLoading: false,
    predictionManualBaseStartDate: undefined,
    predictionManualBaseEndDate: undefined,
    predictionError: false,
    narrativeMessages: [] as NarrativeMessageProps[],
    syncingToPartner: false,
    schedulingInsightsModalIsOpen: false,
    schedulingInsightsModalCloseIconClicked: false,
    findCoverShiftId: null,
  },

  reducers: {
    toggleConflicts: state => {
      state.conflictsEnabled = !state.conflictsEnabled;
    },

    closeJumpstartDrawer: state => {
      state.isJumpstartClosed = true;
      setDrawer(state, null);
    },

    showJumpstartDrawer: (state, { payload: { uxContext } }) => {
      setDrawer(state, DRAWERS.jumpstart_drawer, uxContext);
    },

    resetJumpstartDrawer: state => {
      state.isJumpstartClosed = false;
    },

    showPublishDrawer: (state, { payload: { uxContext } }) => {
      setDrawer(state, DRAWERS.publish_drawer, uxContext);
    },

    showPrintScheduleDrawer: (state, { payload: { uxContext } }) => {
      setDrawer(state, DRAWERS.print_schedule_drawer, uxContext);
    },

    showTemplatesDrawer: state => {
      setDrawer(state, DRAWERS.templates_drawer);
    },

    showSendToPartnerDrawer: (state, { payload: { uxContext } }) => {
      setDrawer(state, DRAWERS.send_to_partner_drawer, uxContext);
    },

    showCopyWeekDrawer: state => {
      setDrawer(state, DRAWERS.copy_week_drawer);
    },

    setPublishLoading: (state, { payload }) => {
      state.publishLoading = payload;
    },

    setPublishedCount: (state, { payload }) => {
      state.publishedCount = payload;
    },

    incrementPublishedCount: state => {
      state.publishedCount += 1;
    },

    hideDrawer: state => {
      if (state.activeDrawer === DRAWERS.publish_drawer)
        state.departmentId = null;

      setDrawer(state, null);
      state.partnerSyncState = undefined;
    },

    hideModal: state => {
      setModal(state, undefined);
    },

    hideDialog: state => {
      setDialog(state, undefined);
    },

    showPredictiveGenerationModal: (
      state,
      { payload: { startDate = undefined } }
    ) => {
      if (startDate) {
        state.predictionManualBaseStartDate = startDate;
      }
      setModal(state, MODALS.predictive_scheduling_generation_modal);
    },

    showPredictiveGenerationFeedbackDialog: state => {
      setDialog(state, DIALOGS.predictive_scheduling_feedback_dialog);
    },

    hidePredictiveGenerationFeedbackDialog: state => {
      setDialog(state, undefined);
    },

    setNarrativeMessages: (state, { payload: { messages } }) => {
      state.narrativeMessages = messages;
    },

    setSchedulePredictionGeneration: (state, { payload: { status } }) => {
      state.schedulePredictionGeneration = status;
    },

    setPredictedShiftsCount: (state, { payload: { count } }) => {
      state.predictedShiftsCount = count;
    },

    openCopyWeekModal: state => {
      state.copyWeekModalIsOpen = true;
    },

    openSchedulingInsightsModal: state => {
      state.schedulingInsightsModalIsOpen = true;
    },

    closeCopyWeekModal: state => {
      state.copyWeekModalIsOpen = false;
    },

    closeSchedulingInsightsModal: state => {
      state.schedulingInsightsModalIsOpen = false;
    },

    closeSchedulingInsightsModalIcon: state => {
      state.schedulingInsightsModalCloseIconClicked = true;
    },

    showNotification: (state, { payload: { message, type, isAI = false } }) => {
      state.notificationCenterMessage = message;
      state.notificationCenterType = type;
      state.notificationCenterMessageAI = isAI;
    },

    hideNotification: state => {
      state.notificationCenterMessage = null;
      state.notificationCenterType = null;
      state.notificationCenterMessageAI = false;
    },

    setCurrentDateRange: (state, { payload: currentDateRange }) => {
      state.currentDateRange = currentDateRange;
    },

    setViewType: (state, { payload: viewType }) => {
      state.viewType = viewType;
    },

    setRangeType: (state, { payload: rangeType }) => {
      state.rangeType = rangeType;
    },

    setEmployeeViewFilter: (state, { payload: employeeViewFilter }) => {
      state.employeeViewFilter = employeeViewFilter;
      state.conflictsEnabled = false;
    },

    setSearchTerm: (state, { payload: searchTerm }) => {
      state.searchTerm = searchTerm;
    },

    loadJobTotals: (state, { payload: pendingJobLaborTotalsIds }) => {
      state.pendingJobLaborTotalsIds = pendingJobLaborTotalsIds;
    },

    setCustomJobIds: (state, { payload: customJobIds }) => {
      state.customJobIds = customJobIds;
    },

    setSortByDay: (state, { payload: sortByDay }) => {
      state.sortByDay = sortByDay;
    },

    setDepartmentId: (state, { payload: { departmentId } }) => {
      state.departmentId = departmentId;
    },

    startCopyPaste: (state, { payload: shiftId }) => {
      state.copyPasteShift = shiftId;
    },

    stopCopyPaste: state => {
      state.copyPasteShift = null;
    },

    updatePendingJobLaborTotalsIds: (state, { payload: id }) => {
      if (state.pendingJobLaborTotalsIds.includes(id) === false) {
        state.pendingJobLaborTotalsIds.push(id);
      }
    },

    updateShowHideAvailabilities: (state, { payload: isChecked }) => {
      state.isAvailabilitiesVisible = isChecked;
    },

    updateShowHideTimeOffs: (state, { payload: isChecked }) => {
      state.isTimeOffsVisible = isChecked;
    },

    updateShowHideShiftsLocationsOnly: (state, { payload: isChecked }) => {
      state.isShiftsAtThisLocationOnly = isChecked;
    },

    setSyncErrors: (state, { payload: syncErrors }) => {
      state.syncErrors = syncErrors;
    },

    setPartnerSyncState: (
      state,
      { payload: syncState }: { payload: SYNC_STATES }
    ) => {
      state.partnerSyncState = syncState;
    },

    setSyncShiftStatus: (state, { payload: syncShiftStatus }) => {
      state.syncShiftStatus = syncShiftStatus;
    },

    toggleSyncingToPartner: (state, { payload: status }) => {
      state.syncingToPartner = status;
    },
    setPredictionBaseStartDate: (state, { payload: { date } }) => {
      state.predictionBaseStartDate = date;
    },
    setPredictionBaseEndDate: (state, { payload: { date } }) => {
      state.predictionBaseEndDate = date;
    },
    setPredictionManualBaseStartDate: (state, { payload: startDate }) => {
      state.predictionManualBaseStartDate = startDate;
    },
    setPredictionManualBaseEndDate: (state, { payload: endDate }) => {
      state.predictionManualBaseEndDate = endDate;
    },

    setPredictionError: (state, { payload: value }) => {
      state.predictionError = value;
    },
    setFindCoverShiftId: (state, { payload }) => {
      state.findCoverShiftId = payload.shiftId;
    },
    clearFindCoverShiftId: state => {
      state.findCoverShiftId = null;
    },
  },

  extraReducers: builder => {
    builder.addCase(fetchSchedule.fulfilled, state => {
      state.hasPendingData = false;
    });

    builder.addCase(createShift.fulfilled, checkForJobUpdate);

    builder.addCase(updateShift.fulfilled, checkForJobUpdate);

    builder.addCase(addTeamActions.UPDATE_EMPLOYEES_SUCCESS, state => {
      state.hasPendingData = true;
    });

    builder.addCase(timeOffsActions.ADD_TIME_OFF_SUCCESS, state => {
      state.hasPendingData = true;
    });

    builder.addCase(addTeamActions.CREATE_EMPLOYEE_SUCCESS, (state, action) => {
      const jobId = action.payload.jobs[0].id;
      // add ID of a new employee to pending job labor array
      // to fetch labor totals for this new Job
      state.pendingJobLaborTotalsIds.push(jobId);
      // Set pending data flag to true to refetch the whole schedule because of a new employee
      state.hasPendingData = true;
    });

    builder.addCase(fetchRoleLaborTotals.fulfilled, (state, action) => {
      const jobIds = action.payload.meta.jobIds;
      state.pendingJobLaborTotalsIds = difference(
        state.pendingJobLaborTotalsIds,
        jobIds
      );
    });

    builder.addCase(fetchCompanyHours.fulfilled, (state, action) => {
      state.companyLaborTotals = action.payload.data;
    });

    builder.addCase(employeeViewActionTypes.TERMINATION_FORM_SUCCESS, state => {
      state.hasPendingData = true;
    });
  },
});

const handleErrors = (
  errors: string[],
  { payload }: SyncShiftProps,
  dispatch: any
) => {
  if (errors.length > 0) {
    if (errors.length === payload.syncedShifts) {
      dispatch(slice.actions.setPartnerSyncState('NONE_SENT'));
    } else {
      dispatch(slice.actions.setPartnerSyncState('PARTIALLY_SENT'));
    }
    dispatch(slice.actions.setSyncErrors(errors));
  } else {
    dispatch(slice.actions.setPartnerSyncState('ERROR'));
  }
};

const handleSuccess = (dispatch: any) => {
  dispatch(slice.actions.setPartnerSyncState('FULLY_SENT'));
};

export const syncShifts = createAsyncThunk(
  'scheduleBuilder/syncShifts',
  async ({ payload }: SyncShiftProps, { dispatch }: { dispatch: any }) => {
    /* reset error state */
    dispatch(slice.actions.setSyncErrors([]));

    try {
      const response = await postJSON(routes.syncShiftsRoute(), {
        ...payload,
      });
      const jobId = response?.job_id;
      if (jobId) {
        pollWorkerStatus(jobId)
          .then((resp: { errors: string }) => {
            const errors = JSON.parse(resp.errors);

            /* on polling finish, handle job errors */
            if (errors.length > 0) {
              handleErrors(errors, { payload }, dispatch);
            } else {
              /* on polling finish, handle job success */
              handleSuccess(dispatch);
            }
          })
          .finally(async () => {
            /* refresh shift data after polling */
            const shiftSyncData = await postJSON(
              routes.syncShiftStatusRoute(),
              {
                start_date: payload.start_date,
                end_date: payload.end_date,
              }
            );
            dispatch(slice.actions.setSyncShiftStatus(shiftSyncData));
          })
          .catch(() => {
            /* on polling failure, display generic error */
            handleErrors([], { payload }, dispatch);
          });
      }
    } catch (error: unknown) {
      /* full query failure, display error message */
      handleErrors([], { payload }, dispatch);
    }
  }
);

export const publishSchedule = createAsyncThunk(
  'scheduleBuilder/publishSchedule',
  async ({ payload }: PublishScheduleProps, { dispatch }) => {
    dispatch(slice.actions.setPublishLoading(true));
    try {
      await postJSON(routes.publishScheduleBuilderScheduleRoute(), payload);

      /* FE-CORE is necessary here to refetch the schedule */
      dispatch(
        fetchSchedule({
          startDate: payload.start_date,
          endDate: payload.end_date,
        })
      );
      const shiftSyncData = await postJSON(routes.syncShiftStatusRoute(), {
        start_date: payload.start_date,
        end_date: payload.end_date,
      });
      dispatch(slice.actions.setSyncShiftStatus(shiftSyncData));
      dispatch(slice.actions.incrementPublishedCount());
    } catch (error) {
      captureException(error);
      dispatch(
        slice.actions.showNotification({
          message: toI18n('errors.generic'),
          type: 'error',
        })
      );
    } finally {
      dispatch(slice.actions.setPublishLoading(false));
    }

    trackUxEvent({
      productArea: PRODUCT_AREAS.SCHEDULE_BUILDER,
      eventCategory: EVENT_CATEGORIES.SCHEDULE_BUILDER_PUBLISH_DRAWER,
      eventAction: EVENT_ACTIONS.SCHEDULE_PUBLISHED,
    });
  }
);

export const {
  showSendToPartnerDrawer,
  showCopyWeekDrawer,
  setDepartmentId,
  hideDrawer,
  hideModal,
  hideDialog,
  setPartnerSyncState,
  showNotification,
  closeJumpstartDrawer,
  showJumpstartDrawer,
  showPredictiveGenerationModal,
  showPredictiveGenerationFeedbackDialog,
  setPredictionManualBaseStartDate,
  setPredictionManualBaseEndDate,
  setSchedulePredictionGeneration,
  setPredictedShiftsCount,
  setPredictionBaseStartDate,
  setPredictionBaseEndDate,
  setPredictionError,
  stopCopyPaste,
  loadJobTotals,
  setNarrativeMessages,
  openSchedulingInsightsModal,
  setFindCoverShiftId,
  clearFindCoverShiftId,
} = slice.actions;

export const { actions, reducer } = slice;

const selectSlice = (state: any) => state.get(SLICE_NAME) || {};

export const selectFindCoverShiftId = (state: any) =>
  selectSlice(state).findCoverShiftId;
